import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TablePagination from "@mui/material/TablePagination"; // Import TablePagination
import TextField from "@mui/material/TextField"; // Import TextField
import Button from "@mui/material/Button"; // Import Button
import config from "../config.json";
import { DataGrid } from "@mui/x-data-grid";
import { useUser } from "../contexts/UserContext";

const TransactionFlow = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { userId } = useUser();
	const { plan_name, plan_id } = location.state || {};
	const [transactions, setTransactions] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedTransaction, setSelectedTransaction] = useState(null);
	const [transactionDetails, setTransactionDetails] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// Sorting state
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("transaction_date");

	// Filter states
	const [filterSource, setFilterSource] = useState("");
	const [filterText, setFilterText] = useState("");
	const [filterTarget, setFilterTarget] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const [filterDate, setFilterDate] = useState("");

	useEffect(() => {
		if (!userId) {
			handleLogout();
		}
	}, [userId, navigate]);

	const handleLogout = () => {
		localStorage.removeItem("authToken");
		localStorage.removeItem("userData");
		navigate("/login");
	};

	useEffect(() => {
		fetch(`${config.baseURL}middlewareTransactions?query=plan_id:${plan_id}`, {
			method: "GET",
			headers: {
				...config.headers,
			},
		})
			.then((response) => response.json())
			.then((data) => setTransactions(data))
			.catch((error) => console.error("Error fetching transactions:", error));
	}, [plan_id]);

	const styles = {
		heading: {
			fontFamily: "Roboto, sans-serif",
			fontSize: "24px",
			marginBottom: "20px",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			marginBottom: "20px",
			fontFamily: "Roboto, sans-serif",
		},
		th: {
			textAlign: "left",
			padding: "10px",
			border: "1px solid #ddd",
			fontSize: "15px",
			backgroundColor: "#2c2d2d",
			color: "white",
		},
		td: {
			textAlign: "left",
			padding: "10px",
			border: "1px solid #ddd",
			fontSize: "14px",
		},
		actionButtonContainer: {
			display: "flex",
			gap: "10px",
			alignItems: "center",
		},
		whiteButton: {
			padding: "8px 12px",
			marginRight: "5px",
			border: "none",
			backgroundColor: "transparent",
			color: "black",
			display: "flex",
			alignItems: "center",
		},
		backButtonContainer: {
			display: "flex",
			justifyContent: "flex-end",
			marginTop: "20px",
		},
		backButton: {
			padding: "10px 20px",
			border: "none",
			cursor: "pointer",
			borderRadius: "4px",
			backgroundColor: "#FF7600",
			color: "#2c2d2d",
			fontSize: "16px",
		},
		modalOverlay: {
			position: "fixed",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			zIndex: 1000,
		},
		modalContent: {
			position: "absolute",
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			backgroundColor: "#fff",
			padding: "20px",
			borderRadius: "8px",
			width: "80%",
			maxWidth: "600px",
		},
		modalCloseButton: {
			padding: "10px 20px",
			border: "none",
			cursor: "pointer",
			borderRadius: "4px",
			backgroundColor: "#FF7600",
			color: "#2c2d2d",
			fontSize: "16px",
		},
	};

	const closeModal = () => {
		setModalOpen(false);
		setSelectedTransaction(null);
		setTransactionDetails([]);
	};

	// Pagination handler functions
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0); // Reset to page 0 when rowsPerPage changes
	};

	// Function to handle sorting when a column header is clicked
	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	// Filter transactions based on filterText
	const filteredTransactions = transactions.filter((transaction) =>
		Object.values(transaction).some((value) =>
			value.toString().toLowerCase().includes(filterText.toLowerCase())
		)
	);

	// Pagination logic first, then sorting within the current page
	const paginatedTransactions = filteredTransactions.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	// Sorting logic based on the selected column
	const sortedTransactions = [...paginatedTransactions].sort((a, b) => {
		if (a[orderBy] < b[orderBy]) {
			return order === "asc" ? -1 : 1;
		}
		if (a[orderBy] > b[orderBy]) {
			return order === "asc" ? 1 : -1;
		}
		return 0;
	});

	return (
		<div>
			<h2 style={styles.heading}>{plan_name} Transaction History</h2>

			{/* New MUI DataGrid Section */}
			<div style={{ height: 500, width: "100%", marginTop: 20 }}>
				<DataGrid
					rows={filteredTransactions}
					columns={[
						{ field: "id", headerName: "ID", width: 90 },
						{ field: "source", headerName: "Source", width: 150 },
						{ field: "target", headerName: "Target", width: 150 },
						{
							field: "transaction_type",
							headerName: "Transaction Type",
							flex: 1,
							minWidth: 150,
						},
						{ field: "transaction_date", headerName: "Date", width: 150 },
						{ field: "status", headerName: "Status", width: 110 },
					]}
					pageSize={5}
					rowsPerPageOptions={[5, 10, 20]}
					disableSelectionOnClick
				/>
			</div>

			<div style={styles.backButtonContainer}>
				<button
					style={styles.backButton}
					onClick={() => navigate("/plan-management")}
				>
					Back
				</button>
			</div>

			{modalOpen && (
				<div style={styles.modalOverlay}>
					<div style={styles.modalContent}>
						<h3>Secondary Flow - Transaction History</h3>
						<table style={styles.table}>
							<thead>
								<tr>
									{transactionDetails.length > 0 &&
										Object.keys(transactionDetails[0]).map((key, index) => (
											<th key={index} style={styles.th}>
												{key}
											</th>
										))}
								</tr>
							</thead>
							<tbody>
								{transactionDetails.map((detail, rowIndex) => (
									<tr key={rowIndex}>
										{Object.values(detail).map((value, colIndex) => (
											<td key={colIndex} style={styles.td}>
												{value || "N/A"}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
						<button style={styles.modalCloseButton} onClick={closeModal}>
							Close
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default TransactionFlow;
